import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import { RootState } from "./index";
import { getThemeSettings } from "../api/settings";

type InitialState = {
    isLoading: boolean;
    theme: string,
    mode: string,
    color: string,
    logo_src: string,
    logo_src_crm: string,
    sign_in: string,
    sign_up: string,
    reset_password: string,
    favicon_src_client: string,
    favicon_src_crm: string,
}

const themes = ['theme-1', 'theme-2', 'theme-3'];

const initialState: InitialState = {
    isLoading: true,

    theme: localStorage.getItem('theme-type') || themes[0],
    color: localStorage.getItem('theme-color') || '#000',
    mode: localStorage.getItem('theme-mode') || 'light',

    logo_src: localStorage.getItem('logo_src'),
    logo_src_crm: localStorage.getItem('logo_src_crm'),
    sign_in: localStorage.getItem('sign_in_src'),
    sign_up: localStorage.getItem('sign_up_src'),
    reset_password: localStorage.getItem('reset_password_src'),
    favicon_src_client: localStorage.getItem('favicon_src_client'),
    favicon_src_crm: localStorage.getItem('favicon_src_crm'),
};

const theme = createSlice({
    name: 'theme',
    initialState,
    reducers: {
        setMode(state, action) {
            state.mode = action.payload
        },
        resetTheme() {
            return initialState
        }
    },
    extraReducers: builder => {
        builder.addCase(getTheme.fulfilled, (state, action) => {
            state.isLoading = false;
            state.theme = action.payload.theme;
            state.color = action.payload.color;
            state.logo_src = action.payload.logo_src;
            state.logo_src_crm = action.payload.logo_src_crm;
            state.favicon_src_crm = action.payload.favicon_src_crm;
            state.favicon_src_client = action.payload.favicon_src_client;
            state.sign_in = action.payload.sign_in;
            state.sign_up = action.payload.sign_up;
            state.reset_password = action.payload.reset_password;
        });
    },
});

export const getTheme = createAsyncThunk('theme/get', async (url: string, { rejectWithValue }) => {
        try {
            const response = await getThemeSettings(url);
            const {
                colorset,
                theme,
                logo_src,
                logo_src_crm,
                favicon_src_crm,
                favicon_src_client,
                sign_in,
                sign_up,
                reset_password
            } = response.data.data;

            const isValidTheme = themes.includes(theme);

            if (colorset) {
                localStorage.setItem('theme-color', colorset);
            }

            if (isValidTheme) {
                localStorage.setItem('theme-type', theme);
            } else {
                console.warn(`getTheme: '${theme}' is invalid theme`);
            }

            localStorage.setItem('logo_src', logo_src);
            localStorage.setItem('logo_src_crm', logo_src_crm);
            localStorage.setItem('sign_in_src', sign_in);
            localStorage.setItem('sign_up_src', sign_up);
            localStorage.setItem('reset_password_src', reset_password);
            localStorage.setItem('favicon_src_client', favicon_src_client);
            localStorage.setItem('favicon_src_crm', favicon_src_crm);

            return {
                theme: isValidTheme ? theme : themes[0],
                color: colorset || '#000',
                logo_src,
                logo_src_crm,
                favicon_src_crm,
                favicon_src_client,
                sign_in,
                sign_up,
                reset_password
            }
        } catch (error: any) {
            return rejectWithValue(error.response.data.message)
        }
    }
);

export const selectTheme = (state: RootState) => state.theme;
export const selectFavicon = createSelector(selectTheme, state => state.favicon_src_client);
export const selectThemeMode = (state: RootState) => state.theme.mode;
export const selectThemeIsLoading = (state: RootState) => state.theme.isLoading;

export const { setMode, resetTheme } = theme.actions;

export const themeReducer =  theme.reducer;
